/**
 * 会话参数抽屉内的语音控制条（T10），随抽屉内容流式布局。
 *
 * 左：语音对话模式开关；右：录音按钮；中间：状态提示。
 * 每个可见部件都通过 `useComponentMeta()` 登记（含 390x844 锚点坐标）。
 *
 * 录音交互：点击麦克风开始录音 → 再点一次结束 → 调用 `src/core/voice` 转写 →
 * 文本填入输入框（不自动发送）。语音对话模式开启后，助手回复会被自动朗读。
 */

import { motion } from 'framer-motion';
import type { ReactElement } from 'react';

import { useMotionPresets } from '../../design/motion';
import { useComponentMeta } from '../../ui';
import { voiceComponentMeta } from './componentMeta';
import { useVoice } from './context';
import styles from './voice.module.css';

/** 状态提示文案。 */
function hintText(voiceMode: boolean, state: string, message: string | undefined): string {
  switch (state) {
    case 'recording':
      return '正在录音，点麦克风结束';
    case 'transcribing':
      return '正在转写…';
    case 'error':
      return message ?? '语音能力调用失败';
    default:
      return voiceMode ? '语音对话已开启：回复将自动朗读' : '点麦克风开始语音输入';
  }
}

export function VoiceComposerControls(): ReactElement {
  const presets = useMotionPresets();
  const { controller, snapshot } = useVoice();
  const { recording } = snapshot;

  const toggleRef = useComponentMeta<HTMLButtonElement>(
    voiceComponentMeta('chat.voiceMode.toggle'),
  );
  const recordRef = useComponentMeta<HTMLButtonElement>(
    voiceComponentMeta('chat.voiceInput.recordButton'),
  );
  const hintRef = useComponentMeta<HTMLDivElement>(
    voiceComponentMeta('chat.voiceInput.statusHint'),
  );
  const hintTextRef = useComponentMeta<HTMLParagraphElement>(
    voiceComponentMeta('chat.voiceInput.statusHint.text'),
  );

  const transcribing = recording.state === 'transcribing';
  const recordingNow = recording.state === 'recording';
  const recordAria = recordingNow ? '结束录音' : transcribing ? '转写中' : '开始录音';
  const hint = hintText(snapshot.voiceMode, recording.state, recording.issue?.message);

  return (
    <div className={styles.composerControls}>
      <motion.button
        ref={toggleRef}
        type="button"
        role="switch"
        aria-checked={snapshot.voiceMode}
        aria-label="语音对话模式"
        className={snapshot.voiceMode ? `${styles.toggle} ${styles.toggleOn}` : styles.toggle}
        data-ui-id="chat.voiceMode.toggle"
        data-ui-role="button"
        whileTap={presets.tap.whileTap}
        transition={presets.tap.transition}
        onClick={() => {
          controller.toggleVoiceMode();
        }}
      >
        <span aria-hidden="true">🗣</span>
      </motion.button>

      <div
        ref={hintRef}
        className={styles.statusHint}
        data-ui-id="chat.voiceInput.statusHint"
        data-ui-role="container"
        data-state={recording.state}
        aria-live="polite"
      >
        <p
          ref={hintTextRef}
          className={styles.statusHintText}
          data-ui-id="chat.voiceInput.statusHint.text"
          data-ui-role="text"
        >
          {hint}
        </p>
      </div>

      <motion.button
        ref={recordRef}
        type="button"
        className={recordingNow ? `${styles.recordButton} ${styles.recording}` : styles.recordButton}
        data-ui-id="chat.voiceInput.recordButton"
        data-ui-role="button"
        data-state={recording.state}
        aria-label={recordAria}
        disabled={transcribing}
        whileTap={presets.tap.whileTap}
        transition={presets.tap.transition}
        onClick={() => {
          if (recordingNow) void controller.stopRecording();
          else void controller.startRecording();
        }}
      >
        <span aria-hidden="true">{recordingNow ? '⏺' : '🎙'}</span>
      </motion.button>
    </div>
  );
}
